import { Eye, Heart, Share2 } from "lucide-react";
import { toast } from "sonner";
import { AISummarySheet } from "@/components/AISummarySheet";

interface TechArticleCardCompactProps {
  image: string;
  title: string;
  description: string;
  views: number;
  liked: boolean;
  likes: number;
  onLike: () => void;
  onClick: () => void;
  articleId?: string;
}

export const TechArticleCardCompact = ({
  image,
  title,
  description,
  views,
  liked,
  likes,
  onLike,
  onClick,
  articleId
}: TechArticleCardCompactProps) => {
  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    onLike();
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const shareData = {
      title: title,
      text: description.substring(0, 100) + "...",
      url: window.location.href,
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success("Articolo condiviso!");
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copiato negli appunti!");
      }
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(window.location.href);
          toast.success("Link copiato negli appunti!");
        } catch {
          toast.error("Impossibile condividere");
        }
      }
    }
  };

  return (
    <div
      onClick={onClick}
      className="flex gap-3 p-3 bg-card rounded-xl border border-border/50 hover:border-primary/30 transition-all duration-300 cursor-pointer hover:shadow-lg group"
    >
      <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </div>
      
      <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
        <div>
          <h3 className="font-semibold text-sm text-foreground line-clamp-1 mb-1">{title}</h3>
          <p className="text-xs text-muted-foreground line-clamp-2">{description}</p>
        </div>
        
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Eye className="w-3 h-3" />
            <span className="text-xs">{views.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center gap-2">
            {articleId && (
              <AISummarySheet
                articleId={articleId}
                articleTitle={title}
                articleDescription={description}
              />
            )}
            
            <button
              onClick={handleShare}
              className="p-1.5 rounded-full hover:bg-muted/50 transition-all duration-300 hover:scale-110 active:scale-90"
            >
              <Share2 className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
            
            <button
              onClick={handleLike}
              className="flex items-center gap-1 transition-all duration-300 hover:scale-110 active:scale-90"
            >
              <Heart 
                className={`w-4 h-4 transition-colors duration-300 ${
                  liked ? 'fill-red-500 text-red-500' : 'text-muted-foreground'
                }`} 
              />
              <span className={`text-xs ${liked ? 'text-red-500' : 'text-muted-foreground'}`}>
                {likes}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
